import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { SHORT_DISCLAIMER } from '@titrate/shared';
import { api, ApiRequestError } from '../lib/api';
import { keys, useMeta } from '../lib/queries';
import { ErrorNotice } from '../components/ui';
import { CountrySelect } from '../components/CountrySelect';
import { browserTimezone, guessCountry } from '../lib/locale';

function AuthShell({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-md py-6">
      <h1 className="text-2xl font-bold tracking-tight text-ink">{title}</h1>
      {subtitle ? <p className="mt-2 text-sm text-muted">{subtitle}</p> : null}
      <div className="card mt-6 p-4 sm:p-6">{children}</div>
    </div>
  );
}

function GoogleButton() {
  const { data: meta } = useMeta();
  if (!meta?.features.googleOAuth) return null;
  return (
    <>
      <a href="/api/auth/google" className="btn-secondary w-full">
        <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.4a5.5 5.5 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.6-5.2 3.6-8.8Z" />
          <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3a7.2 7.2 0 0 1-10.7-3.8h-4v3.1A12 12 0 0 0 12 24Z" />
          <path fill="#FBBC05" d="M5.4 14.3a7.2 7.2 0 0 1 0-4.6v-3.1h-4a12 12 0 0 0 0 10.8l4-3.1Z" />
          <path fill="#EA4335" d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.4 6.6l4 3.1A7.2 7.2 0 0 1 12 4.8Z" />
        </svg>
        Continue with Google
      </a>
      <div className="my-4 flex items-center gap-3">
        <div className="h-px flex-1 bg-line" />
        <span className="text-xs uppercase tracking-wide text-subtle">or</span>
        <div className="h-px flex-1 bg-line" />
      </div>
    </>
  );
}

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { user } = await api.post<{ user: { onboardedAt: string | null } }>('/api/auth/login', { email, password });
      await qc.invalidateQueries({ queryKey: keys.me });
      navigate(user.onboardedAt ? '/dashboard' : '/onboarding');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Sign in failed.');
      setBusy(false);
    }
  };

  const oauthError = params.get('error');

  return (
    <AuthShell title="Sign in">
      {oauthError ? (
        <div className="mb-4">
          <ErrorNotice
            title="Google sign-in unavailable"
            message={
              oauthError === 'google_not_configured'
                ? 'Google sign-in is not configured on this deployment. Use your email and password.'
                : 'We could not complete Google sign-in. Please try again or use your email and password.'
            }
          />
        </div>
      ) : null}
      <GoogleButton />
      <form onSubmit={submit} className="space-y-4">
        {error ? <ErrorNotice message={error} /> : null}
        <div>
          <label className="label" htmlFor="email">Email address</label>
          <input id="email" type="email" required autoComplete="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="password">Password</label>
          <input id="password" type="password" required autoComplete="current-password" className="input" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <div className="mt-5 flex flex-wrap justify-between gap-x-4 gap-y-2 text-sm">
        <Link to="/forgot-password" className="text-brand-700 dark:text-brand-300 hover:underline">Forgot password?</Link>
        <Link to="/register" className="text-brand-700 dark:text-brand-300 hover:underline">Create an account</Link>
      </div>
    </AuthShell>
  );
}

export function Register() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    // Preselected from the browser locale; the user confirms or changes it.
    country: guessCountry(),
    marketingOptIn: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/api/auth/register', {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email,
        password: form.password,
        country: form.country,
        marketingOptIn: form.marketingOptIn,
        ...(browserTimezone() ? { timezone: browserTimezone() } : {}),
      });
      await qc.invalidateQueries({ queryKey: keys.me });
      navigate('/onboarding');
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not create your account.');
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="Your first full-length assessment for each exam is free.">
      <GoogleButton />
      <form onSubmit={submit} className="space-y-4">
        {error ? <ErrorNotice message={error} /> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="rfirst">First name</label>
            <input id="rfirst" required maxLength={80} autoComplete="given-name" className="input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="rlast">Last name</label>
            <input id="rlast" required maxLength={80} autoComplete="family-name" className="input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label" htmlFor="remail">Email address</label>
          <input id="remail" type="email" required autoComplete="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <label className="label" htmlFor="rcountry">Country</label>
          <CountrySelect id="rcountry" required value={form.country} onChange={(country) => setForm({ ...form, country })} />
          <p className="mt-1.5 text-xs text-muted">Where you qualified or are currently based. Used to set your currency and time zone.</p>
        </div>
        <div>
          <label className="label" htmlFor="rpassword">Password</label>
          <input id="rpassword" type="password" required minLength={10} autoComplete="new-password" className="input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <p className="mt-1.5 text-xs text-muted">At least 10 characters.</p>
        </div>
        <label className="flex cursor-pointer gap-3 rounded-lg border border-line p-3">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-brand-700"
            checked={form.marketingOptIn}
            onChange={(e) => setForm({ ...form, marketingOptIn: e.target.checked })}
          />
          <span className="text-sm text-muted">
            Email me study tips and product updates. You can unsubscribe at any time. Account and
            billing emails are sent regardless.
          </span>
        </label>
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? 'Creating your account…' : 'Create account'}
        </button>
      </form>
      <p className="mt-5 text-xs leading-relaxed text-muted">
        By creating an account you agree to our{' '}
        <Link to="/terms" className="underline underline-offset-2">Terms of Service</Link> and{' '}
        <Link to="/privacy" className="underline underline-offset-2">Privacy Policy</Link>.
      </p>
      <p className="mt-3 border-t border-line pt-3 text-xs leading-relaxed text-muted">{SHORT_DISCLAIMER}</p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    // The API always responds identically, so there is nothing to branch on.
    await api.post('/api/auth/forgot-password', { email }).catch(() => undefined);
    setSent(true);
    setBusy(false);
  };

  return (
    <AuthShell title="Reset your password">
      {sent ? (
        <p className="text-sm leading-relaxed text-muted">
          If an account exists for <strong>{email}</strong>, we have sent it a reset link. It expires
          in one hour.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label" htmlFor="femail">Email address</label>
            <input id="femail" type="email" required className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={busy}>
            {busy ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
      )}
      <p className="mt-5 text-sm">
        <Link to="/login" className="text-brand-700 dark:text-brand-300 hover:underline">Back to sign in</Link>
      </p>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/api/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not reset your password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Choose a new password">
      {done ? (
        <p className="text-sm text-muted">
          Your password has been changed.{' '}
          <Link to="/login" className="text-brand-700 dark:text-brand-300 hover:underline">Sign in</Link>
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error ? <ErrorNotice message={error} /> : null}
          {!token ? <ErrorNotice message="This link is missing its token. Request a new reset email." /> : null}
          <div>
            <label className="label" htmlFor="npassword">New password</label>
            <input id="npassword" type="password" required minLength={10} autoComplete="new-password" className="input" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={busy || !token}>
            {busy ? 'Saving…' : 'Change password'}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<'idle' | 'ok' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const qc = useQueryClient();

  const verify = async () => {
    try {
      await api.post('/api/auth/verify-email', { token });
      await qc.invalidateQueries({ queryKey: keys.me });
      setState('ok');
    } catch (err) {
      setMessage(err instanceof ApiRequestError ? err.message : 'Verification failed.');
      setState('error');
    }
  };

  return (
    <AuthShell title="Confirm your email address">
      {state === 'ok' ? (
        <p className="text-sm text-muted">
          Thanks — your email is confirmed.{' '}
          <Link to="/dashboard" className="text-brand-700 dark:text-brand-300 hover:underline">Go to your dashboard</Link>
        </p>
      ) : (
        <div className="space-y-4">
          {state === 'error' ? <ErrorNotice message={message} /> : null}
          <p className="text-sm text-muted">Click below to confirm this email address.</p>
          <button type="button" className="btn-primary w-full" disabled={!token} onClick={() => void verify()}>
            Confirm email address
          </button>
        </div>
      )}
    </AuthShell>
  );
}
